import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { optimizeImage, localSrcSet, localDims } from "../utils/optimizeImage";

/**
 * BannerSlot
 * -----------------------------------------------------------
 * Renders a dynamic advertising banner for the given slot.
 * If no active banner is returned from the backend, falls back
 * to a house promo inviting readers to subscribe to People Who Matter.
 *
 * Usage:
 *   <BannerSlot slot="home-top" />
 *   <BannerSlot slot="home-mid" />
 *   <BannerSlot slot="article-inline" />
 *   <BannerSlot slot="article-footer" />
 */
const BannerSlot = ({ slot, className = "", houseFallback = true }) => {
  const [banner, setBanner] = useState(undefined); // undefined = loading, null = house promo, object = ad

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/banners/${slot}`)
      .then((res) => {
        if (!cancelled) setBanner(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setBanner(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slot]);

  if (banner === undefined) {
    // Reserve consistent vertical space while loading to avoid layout shift.
    return <div className={`w-full ${className}`} style={{ minHeight: 90 }} />;
  }

  if (banner) {
    const handleClick = () => {
      // Fire-and-forget click tracking; don't block navigation.
      axios.post(`${API}/banners/${banner.id}/click`).catch(() => {});
    };
    return (
      <div className={`w-full ${className}`} data-testid={`banner-slot-${slot}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 md:py-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
              <span>Advertisement</span>
              {banner.sponsor_name && !/people who matter/i.test(banner.sponsor_name) ? <span>Sponsored · {banner.sponsor_name}</span> : null}
            </div>
            <a
              href={banner.click_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={handleClick}
              className="block group border border-neutral-200 bg-neutral-50 p-1.5 hover:border-neutral-400 transition-colors"
              data-testid={`banner-link-${banner.id}`}
            >
              <img
                src={optimizeImage(banner.image_url, { width: 1920 })}
                srcSet={localSrcSet(banner.image_url)}
                sizes={localSrcSet(banner.image_url) ? "(min-width: 896px) 896px, 100vw" : undefined}
                alt={banner.name}
                width={banner.image_width || localDims(banner.image_url)?.[0]}
                height={banner.image_height || localDims(banner.image_url)?.[1]}
                loading="lazy"
                decoding="async"
                className="w-full h-auto max-h-[220px] object-contain object-center group-hover:opacity-95 transition-opacity"
              />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // House promo fallback — Subscribe to People Who Matter
  // Rendered as a full-width strip (matches section-to-section rhythm on the homepage).
  // Opt-out via houseFallback={false} to keep the slot silent when no ad is active
  // (used on article-inline so we never show a duplicate subscribe banner).
  if (!houseFallback) return null;

  return (
    <div className={`w-full ${className}`} data-testid={`banner-slot-${slot}-house`}>
      <Link
        to="/#newsletter"
        onClick={(e) => {
          const el = document.getElementById("newsletter");
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: "smooth" });
          }
        }}
        className="block group transition-colors"
        style={{ backgroundColor: "#F1E4C6", borderTop: "3px solid #7A1921" }}
        data-testid={`banner-house-cta-${slot}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <span className="editorial-overline-light block mb-2" style={{ color: "#7A1921", letterSpacing: "0.18em" }}>
                People Who Matter
              </span>
              <h3 className="font-serif text-xl md:text-2xl lg:text-3xl font-bold leading-tight text-neutral-950">
                Get the stories shaping <em className="italic font-light">India&apos;s tomorrow</em> in your inbox.
              </h3>
              <p className="text-neutral-700 text-sm mt-2 max-w-xl">
                Join thousands of leaders reading our weekly editorial digest. No spam, unsubscribe anytime.
              </p>
            </div>
            <span
              className="inline-flex items-center gap-2 px-6 py-3 font-semibold text-xs tracking-[0.16em] uppercase text-white shadow-lg group-hover:opacity-90 transition-opacity self-start md:self-auto flex-shrink-0"
              style={{ backgroundColor: "#0d1117" }}
            >
              Subscribe <span aria-hidden>&rarr;</span>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default BannerSlot;
